import { Alert, Button, Result, Spin } from "antd";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import {
  useMeQuery,
  usePermissionsQuery,
  useRouteAccess,
} from "@/hooks/useAdminQueries";
import { useIdleLogout } from "@/hooks/useIdleLogout";
import { AdminLayout } from "@/layouts/AdminLayout";
import { AuditLogsPage } from "@/pages/audit/AuditLogsPage";
import { LoginPage } from "@/pages/auth/LoginPage";
import { MfaSetupPage } from "@/pages/auth/MfaSetupPage";
import { DashboardPage } from "@/pages/dashboard/DashboardPage";
import { ForbiddenPage } from "@/pages/errors/ForbiddenPage";
import { MonitoringBoardPage } from "@/pages/monitoring/MonitoringBoardPage";
import { ModulePlaceholderPage } from "@/pages/placeholder/ModulePlaceholderPage";
import { OperatorsPage } from "@/pages/operators/OperatorsPage";
import { UserDetailPage } from "@/pages/users/UserDetailPage";
import { UsersListPage } from "@/pages/users/UsersListPage";
import { routeRequiredPermissions } from "@/routes/menuMeta";
import { useAuthStore } from "@/stores/authStore";

function FullscreenSpin() {
  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Spin size="large" />
    </div>
  );
}

function RequireToken() {
  const token = useAuthStore((s) => s.accessToken);
  if (!token)
    return <Navigate to="/login" replace state={{ from: "/dashboard" }} />;
  return <Outlet />;
}

function RequireMfaBound() {
  const me = useMeQuery(true);

  if (me.isPending) return <FullscreenSpin />;
  const bound = Boolean(me.data?.mfa_bound);

  if (me.isError) {
    return (
      <div style={{ padding: 48 }}>
        <Result
          status="warning"
          title="无法读取当前账号信息"
          subTitle={me.error instanceof Error ? me.error.message : "Network error"}
          extra={
            <Button type="primary" onClick={() => me.refetch()}>
              重试
            </Button>
          }
        />
      </div>
    );
  }

  if (!bound) return <Navigate to="/mfa-setup" replace />;

  return <Outlet />;
}

function RoutePermissionBoundary() {
  const { pathname } = useLocation();
  const pq = usePermissionsQuery(true);
  const required = routeRequiredPermissions(pathname);

  const access = useRouteAccess(required, pq.data);

  if (!pq.data && pq.isPending) return <FullscreenSpin />;
  if (pq.isError) {
    return (
      <div style={{ padding: 48 }}>
        <Alert
          type="error"
          message="获取权限菜单失败（网络或 401）；请刷新或重新登录。"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Button type="primary" onClick={() => pq.refetch()}>
          重试加载权限
        </Button>
      </div>
    );
  }

  if (access === "deny") return <ForbiddenPage />;
  return <Outlet />;
}

function IdleScope() {
  useIdleLogout(true);
  return <Outlet />;
}

function FallbackNavigate() {
  const token = useAuthStore((s) => s.accessToken);
  return <Navigate to={token ? "/dashboard" : "/login"} replace />;
}

export function AppRoutes() {
  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  const token = useAuthStore((s) => s.accessToken);

  if (!bootstrapped) return <FullscreenSpin />;

  return (
    <Routes>
      <Route
        path="/login"
        element={token ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />
      <Route element={<RequireToken />}>
        <Route path="/mfa-setup" element={<MfaSetupPage />} />
        <Route element={<RequireMfaBound />}>
          <Route element={<IdleScope />}>
            <Route element={<AdminLayout />}>
              <Route element={<RoutePermissionBoundary />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/users" element={<UsersListPage />} />
                <Route path="/users/:userId" element={<UserDetailPage />} />
                <Route path="/audit-logs" element={<AuditLogsPage />} />
                <Route
                  path="/vpn/monitoring"
                  element={<MonitoringBoardPage />}
                />
                <Route
                  path="/vpn/protocols"
                  element={<ModulePlaceholderPage />}
                />
                <Route
                  path="/vpn/profiles"
                  element={<ModulePlaceholderPage />}
                />
                <Route path="/vpn/nodes" element={<ModulePlaceholderPage />} />
                <Route path="/operators" element={<OperatorsPage />} />
                <Route path="/channels" element={<ModulePlaceholderPage />} />
                <Route path="/releases" element={<ModulePlaceholderPage />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>
      <Route path="/" element={<FallbackNavigate />} />
      <Route path="*" element={<FallbackNavigate />} />
    </Routes>
  );
}
